import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { ApiError } from '@crave/api-client';
import { api } from '../api.ts';
import { QrCode } from '../components/QrCode.tsx';

/**
 * Logging reviews at a tasting event (#62). Someone tries a slice, says what they think, and it is
 * in Crave before they walk away: stars, would they order it, what they said. The QR button puts the
 * same form on their own phone for the people who would rather answer it themselves.
 */

export interface Batch {
  id: string;
  batch_code: string;
  campaign: string;
  item_name: string;
  pizzas: number;
  slices_per_pizza: number;
  responses: number;
  avg_overall: number | null;
  yes: number;
  taste_url: string;
}

const AGAIN: [string, string][] = [
  ['YES', 'Yes'],
  ['MAYBE', 'Maybe'],
  ['NO', 'No'],
];

const CSS = `
.ts { display: flex; flex-direction: column; height: 100%; min-height: 0; }
.ts-head { position: sticky; top: 0; z-index: 2; background: var(--ivory); border-bottom: 1px solid var(--line);
  padding: 12px 16px; display: flex; gap: 10px; align-items: center; flex-wrap: wrap; }
.ts-pizzas { display: flex; gap: 8px; flex-wrap: wrap; margin-right: auto; }
.ts-pizza { min-height: 44px; padding: 0 14px; border-radius: 12px; border: 1px solid var(--line);
  background: var(--paper); font: inherit; font-weight: 600; font-size: 16px; cursor: pointer; }
.ts-pizza[aria-pressed='true'] { background: var(--ink); border-color: var(--ink); color: #fff; }
.ts-qr { min-height: 44px; padding: 0 16px; border-radius: 12px; border: 1px solid var(--line);
  background: var(--paper); font: inherit; font-weight: 600; font-size: 16px; cursor: pointer; }
.ts-body { flex: 1; min-height: 0; overflow-y: auto; padding: 16px; }
.ts-form { max-width: 720px; margin: 0 auto; display: grid; gap: 18px; }
.ts-label { font-size: 15px; font-weight: 700; color: var(--ink); }
.ts-stars { display: flex; gap: 10px; }
.ts-star { flex: 1; aspect-ratio: 1 / 1; max-width: 104px; min-height: 64px; border-radius: 18px; border: 1px solid var(--line);
  background: var(--paper); font-size: 34px; line-height: 1; color: var(--line-strong, #C9C6BF); cursor: pointer; }
.ts-star[data-on='true'] { background: #FFF6E5; border-color: #E8B93B; color: #E8B93B; }
.ts-again { display: flex; gap: 10px; }
.ts-again button { flex: 1; min-height: 60px; border-radius: 16px; border: 1px solid var(--line); background: var(--paper);
  font: inherit; font-size: 18px; font-weight: 700; cursor: pointer; }
.ts-again button[aria-pressed='true'] { background: var(--ink); border-color: var(--ink); color: #fff; }
.ts-text { width: 100%; box-sizing: border-box; min-height: 84px; padding: 12px 14px; border-radius: 14px; border: 1px solid var(--line);
  font: inherit; font-size: 17px; background: var(--paper); color: var(--ink); resize: vertical; }
.ts-who { display: grid; gap: 10px; grid-template-columns: 1fr 1fr; }
@media (max-width: 560px) { .ts-who { grid-template-columns: 1fr; } }
.ts-who input { width: 100%; box-sizing: border-box; min-height: 52px; padding: 0 14px; border-radius: 14px; border: 1px solid var(--line);
  font: inherit; font-size: 17px; background: var(--paper); color: var(--ink); }
.ts-foot { position: sticky; bottom: 0; background: var(--ivory); border-top: 1px solid var(--line);
  padding: 12px 16px; display: flex; gap: 12px; align-items: center; flex-wrap: wrap; }
.ts-tally { margin-right: auto; font-size: 15px; color: var(--ink-soft); font-variant-numeric: tabular-nums; }
.ts-save { min-height: 58px; padding: 0 28px; border: 0; border-radius: 16px; background: var(--crave-red); color: #fff;
  font: inherit; font-size: 19px; font-weight: 700; cursor: pointer; }
.ts-save:disabled { opacity: .45; cursor: default; }
.ts-undo { min-height: 48px; padding: 0 16px; border-radius: 12px; border: 1px solid var(--line);
  background: var(--paper); font: inherit; font-weight: 600; cursor: pointer; }
.ts-note { margin: 0; color: var(--ink-soft); font-size: 14px; }
.ts-sheet { border: 0; padding: 0; margin: auto; border-radius: 24px; background: #fff; color: var(--ink);
  width: min(560px, calc(100vw - 24px)); box-shadow: 0 24px 60px rgba(20,20,20,.3); }
.ts-sheet::backdrop { background: rgba(20,20,20,.6); }
.ts-sheet-body { display: grid; gap: 14px; padding: 26px; justify-items: center; text-align: center; }
.ts-empty { max-width: 520px; margin: 12vh auto; text-align: center; color: var(--ink-soft); font-size: 18px; line-height: 1.5; }
`;

export function Tasting({ location }: { location: string }) {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['tastings', location],
    queryFn: async () =>
      (
        await api.get<{ batches: Batch[] }>(
          `/staff/tastings?location=${encodeURIComponent(location)}`,
        )
      ).data,
    refetchInterval: 60_000,
  });
  const batches = list.data?.batches ?? [];
  const [chosen, setChosen] = useState<string | null>(null);
  const batch = batches.find((b) => b.id === chosen) ?? batches[0] ?? null;

  const [overall, setOverall] = useState<number | null>(null);
  const [again, setAgain] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [lastId, setLastId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const qrRef = useRef<HTMLDialogElement>(null);
  const [qrOpen, setQrOpen] = useState(false);

  useEffect(() => {
    const d = qrRef.current;
    if (!d) return;
    if (qrOpen && !d.open) d.showModal();
    if (!qrOpen && d.open) d.close();
  }, [qrOpen]);

  const clear = () => {
    setOverall(null);
    setAgain(null);
    setComment('');
    setFirstName('');
    setEmail('');
  };

  const save = useMutation({
    mutationFn: async () => {
      const b = batch!;
      const res = await api.post<{ id: string }>(`/staff/tastings/batches/${b.id}/reviews`, {
        overall,
        ...(again ? { would_order: again } : {}),
        ...(comment.trim() ? { comment: comment.trim() } : {}),
        ...(firstName.trim() ? { first_name: firstName.trim() } : {}),
        ...(email.trim() ? { contact_email: email.trim() } : {}),
      });
      return res.data;
    },
    onSuccess: (d) => {
      setLastId(d.id);
      setError(null);
      clear();
      void qc.invalidateQueries({ queryKey: ['tastings', location] });
    },
    onError: (e) =>
      setError(e instanceof ApiError ? e.message : 'It did not save. Check the connection.'),
  });

  const undo = useMutation({
    mutationFn: async () => api.delete(`/staff/tastings/reviews/${lastId!}`),
    onSuccess: () => {
      setLastId(null);
      void qc.invalidateQueries({ queryKey: ['tastings', location] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'It could not be taken back.'),
  });

  const css = (
    <style href="crave-tasting" precedence="default">
      {CSS}
    </style>
  );

  if (list.isSuccess && batches.length === 0)
    return (
      <>
        {css}
        <p className="ts-empty">
          Nothing is being tasted here right now. Start a campaign and bake a batch under Tastings
          in admin, and this screen fills in.
        </p>
      </>
    );
  if (!batch)
    return (
      <>
        {css}
        <p className="ts-empty">{list.isError ? 'Could not load the tasting.' : 'Loading…'}</p>
      </>
    );

  const yes = batch.responses > 0 ? Math.round((batch.yes / batch.responses) * 100) : 0;
  return (
    <div className="ts">
      {css}
      <div className="ts-head">
        {batches.length > 1 ? (
          <div className="ts-pizzas" role="group" aria-label="Which pizza">
            {batches.map((b) => (
              <button
                key={b.id}
                type="button"
                className="ts-pizza"
                aria-pressed={b.id === batch.id}
                onClick={() => {
                  setChosen(b.id);
                  clear();
                  setLastId(null);
                }}
              >
                {b.item_name}
              </button>
            ))}
          </div>
        ) : (
          <div className="ts-pizzas">
            <strong style={{ fontSize: 18 }}>{batch.item_name}</strong>
            <span style={{ alignSelf: 'center', color: 'var(--ink-soft)' }}>{batch.campaign}</span>
          </div>
        )}
        <button type="button" className="ts-qr" onClick={() => setQrOpen(true)}>
          Show the QR
        </button>
      </div>

      <div className="ts-body">
        <form
          className="ts-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (overall) save.mutate();
          }}
        >
          <div>
            <div className="ts-label">How was it?</div>
            <div className="ts-stars" role="group" aria-label="Stars out of five">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  className="ts-star"
                  data-on={overall !== null && n <= overall}
                  aria-label={`${n} of 5`}
                  aria-pressed={overall === n}
                  onClick={() => setOverall(n)}
                >
                  ★
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="ts-label">Would they order it?</div>
            <div className="ts-again" role="group" aria-label="Would they order it">
              {AGAIN.map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  aria-pressed={again === value}
                  onClick={() => setAgain(again === value ? null : value)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="ts-label">What they said (optional)</div>
            <textarea
              className="ts-text"
              value={comment}
              maxLength={1000}
              placeholder="Their words, not a summary."
              onChange={(e) => setComment(e.target.value)}
            />
          </div>

          <div>
            <div className="ts-label">Want to hear when it hits the menu? (optional)</div>
            <div className="ts-who">
              <input
                value={firstName}
                maxLength={60}
                placeholder="First name"
                autoComplete="off"
                onChange={(e) => setFirstName(e.target.value)}
              />
              <input
                value={email}
                type="email"
                maxLength={200}
                placeholder="Email"
                autoComplete="off"
                autoCapitalize="none"
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
          </div>
          {error && (
            <p className="ts-note" style={{ color: 'var(--crave-red)', fontWeight: 600 }}>
              {error}
            </p>
          )}
        </form>
      </div>

      <div className="ts-foot">
        <span className="ts-tally">
          {batch.responses === 0
            ? 'No reviews yet.'
            : `${batch.responses} ${batch.responses === 1 ? 'review' : 'reviews'} · ${
                batch.avg_overall?.toFixed(1) ?? '—'
              } average · ${yes}% would order it`}
        </span>
        {lastId && (
          <button
            type="button"
            className="ts-undo"
            disabled={undo.isPending}
            onClick={() => undo.mutate()}
          >
            Undo the last one
          </button>
        )}
        <button
          type="button"
          className="ts-save"
          disabled={!overall || save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending ? 'Saving…' : 'Save review'}
        </button>
      </div>

      <dialog
        ref={qrRef}
        className="ts-sheet"
        aria-label="Tasting QR code"
        onCancel={(e) => {
          e.preventDefault();
          setQrOpen(false);
        }}
      >
        {qrOpen && (
          <div className="ts-sheet-body">
            <strong style={{ fontSize: 24 }}>Rate the {batch.item_name}</strong>
            <QrCode value={batch.taste_url} size={260} />
            <p className="ts-note">
              Point a phone camera at this. Everyone who tried it can answer, as many people as you
              like.
            </p>
            <button type="button" className="ts-save" onClick={() => setQrOpen(false)}>
              Done
            </button>
          </div>
        )}
      </dialog>
    </div>
  );
}
